import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { scroller } from 'react-scroll';
import contactQuery from 'mutations/contact';
import verifyCaptchaQuery from 'mutations/verifyCaptcha';
import { performMutation } from 'lib/api';

import Cta from 'components/Core/Cta';

import Input from './Input';
import Textarea from './Textarea';
import {
  FormRow as FormRowElem,
  FormImageWrap as FormImageWrapElem,
  Notification as NotificationElem,
} from './Contact.styles';

const formItems = [
  {
    label: 'Naam',
    name: 'name',
    required: true,
    type: 'text',
    Component: Input,
    validationObject: {
      required: 'Naam is verplicht',
    },
  },
  {
    label: 'E-mailadres',
    name: 'email',
    required: true,
    type: 'email',
    Component: Input,
    validationObject: {
      required: 'Email is verplicht',
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'Ongeldig email adres',
      },
    },
  },
  {
    label: 'Telefoonnummer',
    name: 'phone',
    required: false,
    type: 'tel',
    Component: Input,
    validationObject: {
      required: false,
    },
  },
];

const additionalFieldConf = {
  required: true,
  Component: Textarea,
  validationObject: {
    required: 'Dit veld is verplicht',
    minLength: 1,
    maxLength: 500,
  },
};

const Form = ({ title, image, form, hostname, captchaKey }) => {
  const SITE_KEY = captchaKey;
  const { register, handleSubmit, errors } = useForm();
  const [stateValue, setStateValue] = useState({
    notificationType: null,
    notificationMessage: null,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { id: formId } = form;

  const onSubmit = async (data) => {
    const { phone, name, email, ...remainingFields } = data;
    const extraFields = [];

    for (let i = 0; i < Object.entries(remainingFields).length; i += 1) {
      const [key, value] = Object.entries(remainingFields)[i];
      extraFields.push({ name: key, value });
    }

    const newData = {
      phone,
      name,
      email,
      extraFields,
    };

    try {
      const { contact } = await performMutation(contactQuery, { hostname, formId, ...newData });
      if (contact.errors.length) {
        throw new Error(contact.errors[0].message);
      } else {
        setStateValue({
          notificationType: null,
          notificationMessage: null,
        });
        setIsSubmitted(true);

        // Scroll to see the success message
        scroller.scrollTo('contact-form', {
          duration: 1500,
          delay: 100,
          smooth: 'easeInOutQuint',
        });
      }
    } catch (err) {
      setStateValue({
        notificationType: 'error',
        notificationMessage: 'Formulier fout!',
      });
    }
  };

  const verifyToken = async (token, data) => {
    try {
      const {
        verifyCaptcha: { success, action },
      } = await performMutation(verifyCaptchaQuery, { token, hostname });
      if (success && action === 'submit') {
        onSubmit(data);
      } else {
        setStateValue({
          notificationType: 'error',
          notificationMessage: 'Captcha validation failed!',
        });
      }
    } catch (err) {
      setStateValue({
        notificationType: 'error',
        notificationMessage: 'Captcha validation failed!',
      });
    }
  };

  const invokeChallenge = (data) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(SITE_KEY, { action: 'submit' }).then((token) => {
        verifyToken(token, data);
      });
    });
  };

  useEffect(() => {
    // Add reCaptcha
    const script = document.createElement('script'); // eslint-disable-line no-undef
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    document.body.appendChild(script); // eslint-disable-line no-undef
  }, []);

  const { notificationType, notificationMessage } = stateValue;
  const hideFormError = notificationType && notificationMessage;

  // Hide the notification if there is error.
  if (errors && errors.email && notificationType && notificationMessage) {
    setStateValue({
      notificationType: null,
      notificationMessage: null,
    });
  }

  return (
    <FormRowElem className="u-block-margins relative">
      <FormImageWrapElem>
        <LazyLoadImage
          className="block w-full h-full object-cover object-center"
          src={image.xl.url}
          alt={image.title}
        />
      </FormImageWrapElem>

      <div id="contact-form" className="container px-4 pt-4 mx-auto h-full">
        <div className="flex flex-wrap items-center h-full -mx-4">
          <div className="flex-full md:flex-1/2 md:max-w-1/2 px-4 mb-4 md:hidden">
            <LazyLoadImage
              className="block w-full h-full object-cover object-center rounded-base"
              src={image.sm.url}
              alt={image.title}
            />
          </div>
          {!isSubmitted ? (
            <form
              onSubmit={handleSubmit(invokeChallenge)}
              className="flex-full max-w-full md:flex-1/2 md:max-w-1/2 px-4 md:pl-8 md:ml-auto"
            >
              <h2 className="pb-4 md:pb-6 lg:pb-8">{title}</h2>

              {/* Render the fields that are always available (default fields) */}
              {formItems.map((item) => (
                <div key={item.name} className="relative mb-4">
                  <item.Component {...item} ref={register(item.validationObject)} />
                  {errors && errors[item.name] && !hideFormError && (
                    <NotificationElem type="error">{errors[item.name].message}</NotificationElem>
                  )}
                </div>
              ))}

              {/* Render additional fields created in the CMS */}
              {form.formFields &&
                Array.from(form.formFields).map((item) => (
                  <div key={item.name} className="relative mb-4">
                    <additionalFieldConf.Component
                      {...{ name: item.name, label: item.label, ...additionalFieldConf }}
                      ref={register(additionalFieldConf.validationObject)}
                    />
                    {errors && errors[item.name] && !hideFormError && (
                      <NotificationElem type="error">{errors[item.name].message}</NotificationElem>
                    )}
                  </div>
                ))}

              <Cta type="submit" showIcon={false} className="w-1/2 xl:w-1/3">
                Verstuur
              </Cta>
              {notificationType && notificationMessage && (
                <NotificationElem type={notificationType}>{notificationMessage}</NotificationElem>
              )}
              <div className="g-recaptcha" data-sitekey={SITE_KEY} data-size="invisible" />
            </form>
          ) : (
            <div className="flex-full max-w-full md:flex-1/2 md:max-w-1/2 px-4 md:pl-8 md:ml-auto">
              <h2 className="h3 pb-4 md:pb-6 lg:pb-8">Formulier met succes verstuurd</h2>
            </div>
          )}
        </div>
      </div>
    </FormRowElem>
  );
};

Form.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.string,
    formFields: PropTypes.shape({
      label: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  }).isRequired,
  title: PropTypes.string,
  captchaKey: PropTypes.string.isRequired,
  image: PropTypes.shape({
    title: PropTypes.string,
    sm: PropTypes.shape({
      url: PropTypes.string,
    }),
    xl: PropTypes.shape({
      url: PropTypes.string,
    }),
  }).isRequired,
  hostname: PropTypes.string.isRequired,
};

Form.defaultProps = {
  title: 'Neem contact met ons op',
};

export default Form;
